import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Button, Empty, Input, Segmented, Spin } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import config from '../../config';
import { BottomNavigation } from './components/mobile/BottomNavigation';
import { ClientCard } from './components/clients/ClientCard';
import { ClientDetailScreen } from './components/clients/ClientDetailScreen';
import { PeriodTabs } from './components/clients/PeriodTabs';
import { datesFor, formatDate, integer, n } from './utils/mobileHelpers';
import './mobileClients.css';

// PAGE_SIZE (20) et les libellés de période diffèrent de ceux de mobileHelpers : ils restent locaux.
const PAGE_SIZE = 20;
const PERIODS = [
  { key: 'today', label: "Aujourd’hui" },
  { key: 'yesterday', label: 'Hier' },
  { key: 'week', label: '7 jours' },
  { key: 'month', label: 'Ce mois' },
];
const MODES = [
  { key: 'active', label: 'Actives' },
  { key: 'best', label: 'Meilleures' },
  { key: 'relance', label: 'À relancer' },
  { key: 'new', label: 'Nouvelles' },
];

export default function MobileClients() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.user?.currentUser);
  const role = currentUser?.role;
  const token = currentUser?.accessToken || currentUser?.token;
  const domain = config.REACT_APP_SERVER_DOMAIN;
  // period, mode, q et detail vivent dans l'URL, comme pour MobileActions.
  const period = PERIODS.some((item) => item.key === searchParams.get('period')) ? searchParams.get('period') : 'month';
  const mode = MODES.some((item) => item.key === searchParams.get('mode')) ? searchParams.get('mode') : 'active';
  const submittedQuery = searchParams.get('q') || '';
  const detailToken = searchParams.get('detail') || '';
  const detailId = detailToken.startsWith('client:') ? detailToken.slice('client:'.length) : '';
  const [query, setQuery] = useState(submittedQuery);
  const [state, setState] = useState({ loading: true, loadingMore: false, items: [], meta: {}, error: '' });
  const [page, setPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detail, setDetail] = useState({ loading: false, data: null, error: '' });
  const [loyalty, setLoyalty] = useState(null);
  const dates = useMemo(() => datesFor(period), [period]);
  const headers = useMemo(() => token ? { authorization: `Bearer ${token}`, token: `Bearer ${token}` } : {}, [token]);

  const updateQuery = useCallback((patch, { replace = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    navigate(`/mobile?${next.toString()}`, { replace });
  }, [navigate, searchParams]);

  useEffect(() => setQuery(submittedQuery), [submittedQuery]);

  const fetchPage = useCallback(async (targetPage, append) => {
    if (role !== 'admin') return;
    setState((current) => ({ ...current, loading: !append, loadingMore: append, error: '' }));
    try {
      const response = await axios.get(`${domain}/api/mobile-actions/clientes`, {
        headers,
        params: { ...dates, mode, search: submittedQuery || undefined, page: targetPage, page_size: PAGE_SIZE },
      });
      const incoming = response.data?.items || [];
      setState((current) => {
        const base = append ? current.items : [];
        const seen = new Set(base.map((item) => item.id));
        const merged = [...base, ...incoming.filter((item) => !seen.has(item.id))];
        return { loading: false, loadingMore: false, items: merged, meta: response.data?.meta || {}, error: '' };
      });
      setPage(targetPage);
    } catch (error) {
      setState((current) => ({
        loading: false,
        loadingMore: false,
        items: append ? current.items : [],
        meta: append ? current.meta : {},
        error: error?.response?.data?.error || 'Impossible de charger les clientes.',
      }));
    }
  }, [dates, domain, headers, mode, role, submittedQuery]);

  const load = useCallback(() => fetchPage(1, false), [fetchPage]);
  const loadMore = useCallback(() => fetchPage(page + 1, true), [fetchPage, page]);

  useEffect(() => { load(); }, [load]);

  // Fiche cliente pilotée par l'URL : ?section=clientes&detail=client:{id}
  useEffect(() => {
    if (role !== 'admin' || !detailId) return undefined;
    let cancelled = false;
    setDetail({ loading: true, data: null, error: '' });
    axios.get(`${domain}/api/mobile-actions/details/client/${detailId}`, { headers, params: dates })
      .then((response) => { if (!cancelled) setDetail({ loading: false, data: response.data, error: '' }); })
      .catch((error) => {
        if (cancelled) return;
        const text = error?.response?.data?.error || 'Impossible de charger cette cliente.';
        setDetail({ loading: false, data: null, error: text });
      });
    // Solde fidélité (best-effort, non bloquant) : le programme peut être désactivé.
    setLoyalty(null);
    axios.get(`${domain}/api/whatsapp/loyalty/client/${detailId}`, { headers })
      .then((response) => { if (!cancelled) setLoyalty(response.data); })
      .catch(() => { if (!cancelled) setLoyalty(null); });
    return () => { cancelled = true; };
  }, [dates, detailId, domain, headers, role]);

  const openDetail = (item) => {
    setSelectedItem(item);
    const next = new URLSearchParams(searchParams);
    next.set('detail', `client:${item.id}`);
    navigate(`/mobile?${next.toString()}`, { state: { from: `${location.pathname}${location.search}` } });
  };

  const closeDetail = () => {
    if (location.state?.from) navigate(-1);
    else updateQuery({ detail: '' }, { replace: true });
  };

  if (role !== 'admin') return <Navigate to="/" replace />;

  if (detailId) {
    const record = detail.data?.record || {};
    const selected = (selectedItem && String(selectedItem.id) === String(detailId) ? selectedItem : null)
      || state.items.find((item) => String(item.id) === String(detailId))
      || record;
    return (
      <section className="mobile-clients-page ndoe-with-bottom-nav">
        <div className="mobile-clients-shell">
          <ClientDetailScreen selected={selected} detail={detail} onBack={closeDetail} loyalty={loyalty} />
        </div>
        <BottomNavigation navigate={navigate} />
      </section>
    );
  }

  const modeLabel = MODES.find((item) => item.key === mode)?.label || 'Actives';
  const periodLabel = PERIODS.find((item) => item.key === period)?.label || 'Ce mois';
  const heroCaption = mode === 'relance'
    ? `Opportunités au ${formatDate(dates.end_date)}`
    : `${modeLabel} · ${periodLabel}`;
  const relaunchHint = [
    `Sélection : au moins ${state.meta.relaunch_min_net_pairs || 3} paires nettes,`,
    `${state.meta.relaunch_min_orders || 2} commandes`,
    `et aucun achat depuis ${state.meta.relaunch_inactivity_days || 90} jours.`,
  ].join(' ');

  return (
    <section className="mobile-clients-page ndoe-with-bottom-nav">
      <div className="mobile-clients-shell">
        <header className="mobile-clients-header">
          <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/mobile?period=${period}`)} />
          <div>
            <span>NDOÉ MOBILE · RELATION CLIENTE</span>
            <h1>Clientes</h1>
            <p>Achats, fidélité et opportunités de relance.</p>
          </div>
          <Button shape="circle" icon={<ReloadOutlined />} onClick={load} loading={state.loading} />
        </header>
        <PeriodTabs periods={PERIODS} value={period} onChange={(key) => updateQuery({ period: key })} />
        <Segmented
          className="mobile-client-mode-switch"
          value={mode}
          onChange={(value) => updateQuery({ mode: value })}
          options={MODES.map((item) => ({ value: item.key, label: item.label }))}
        />
        <form
          className="mobile-client-search"
          onSubmit={(event) => { event.preventDefault(); updateQuery({ q: query.trim() }); }}
        >
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Nom, téléphone ou commune"
          />
          <Button htmlType="submit" type="primary">Rechercher</Button>
        </form>
        {mode === 'relance' && <Alert className="mobile-client-hint" type="info" showIcon message={relaunchHint} />}
        {state.error && <Alert type="warning" showIcon message={state.error} />}
        {state.loading ? (
          <div className="mobile-clients-loading">
            <Spin size="large" />
            <span>Chargement des clientes…</span>
          </div>
        ) : (
          <>
            <section className="mobile-client-hero">
              <TeamOutlined />
              <div>
                <span>{modeLabel.toUpperCase()}</span>
                <strong>{integer(state.meta.total)}</strong>
                <p>{heroCaption}</p>
              </div>
            </section>
            <section className="mobile-client-list">
              {state.items.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune cliente ne correspond à ce segment." />
              ) : (
                state.items.map((item) => <ClientCard key={item.id} item={item} mode={mode} onOpen={openDetail} />)
              )}
            </section>
            {n(state.meta.total) > state.items.length && (
              <div className="mobile-client-loadmore" style={{ marginTop: 12 }}>
                <Button block loading={state.loadingMore} onClick={loadMore}>
                  Voir plus · {integer(state.items.length)}/{integer(state.meta.total)}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <BottomNavigation navigate={navigate} />
    </section>
  );
}
